import React from 'react';
import { Box, Typography, Grid } from '@mui/joy';
import { useWindowBasketStore } from '../hooks/useWindowBasket';
import { getWindowTypeName, getWindowImagePath } from '../utils/windowHelpers';

interface WindowOrderSummaryProps {
  compact?: boolean;
}

const WindowOrderSummary: React.FC<WindowOrderSummaryProps> = ({ compact = false }) => {
  const { items, customerReference } = useWindowBasketStore();
  
  if (items.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography level="body-lg">No windows in your order</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography level={compact ? 'h4' : 'h3'} mb={2}>Order Summary</Typography>
      
      {/* Display customer reference */}
      {customerReference && (
        <Typography level="body-md" mb={2}>
          Customer Reference: {customerReference}
        </Typography>
      )}
      
      <Typography level="body-sm" mb={2}>
        {items.length} {items.length === 1 ? 'window' : 'windows'} in order
        ({items.reduce((count, item) => count + item.quantity, 0)} total units)
      </Typography>
      
      {!compact && (
        <Box sx={{ maxHeight: compact ? '200px' : '300px', overflow: 'auto', mb: 2 }}>
          {items.map(item => (
            <Box 
              key={item.id} 
              sx={{ 
                mb: 2, 
                p: 2, 
                borderRadius: 'sm',
                bgcolor: 'background.level1',
              }}
            >
              <Grid container spacing={2}>
                <Grid xs={12} sm={4}>
                  <Box sx={{ 
                    width: '100%', 
                    height: 200,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 'md',
                    backgroundColor: '#f9f9f9',
                    p: 2,
                    mb: { xs: 2, sm: 0 }
                  }}>
                    <Box 
                      component="img"
                      src={getWindowImagePath(item.windowType, item.windowStyle, item.imagePath)}
                      alt={`${item.windowType} Style ${item.windowStyle}`}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain'
                      }}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        // Fallback if image fails to load
                        console.error(`Image failed to load for window ${item.id}, trying fallback`);
                        // Try generic fallback image
                        const fallbackSrc = '/images/windows/Casements/Traditional/Traditional Style One.jpg';
                        if (fallbackSrc !== e.currentTarget.src) {
                          e.currentTarget.src = fallbackSrc;
                        } else {
                          // If that's already what we tried, hide the image and show a placeholder
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            const placeholder = document.createElement('div');
                            placeholder.style.width = '100%';
                            placeholder.style.height = '100%';
                            placeholder.style.display = 'flex';
                            placeholder.style.alignItems = 'center';
                            placeholder.style.justifyContent = 'center';
                            placeholder.style.backgroundColor = '#f0f0f0';
                            placeholder.style.color = '#888';
                            placeholder.style.fontStyle = 'italic';
                            placeholder.textContent = `${item.windowType} - Style ${item.windowStyle}`;
                            parent.appendChild(placeholder);
                          }
                        }
                      }}
                    />
                  </Box>
                </Grid>
                
                <Grid xs={12} sm={8}>
                  {/* Display window reference */}
                  <Typography level="body-sm" fontWeight="bold" color="primary">
                    Reference: {item.reference || 'No reference'}
                  </Typography>
                  <Typography level="body-sm" fontWeight="bold">
                    {item.windowType} - Style {item.windowStyle}
                  </Typography>
                  {(item.width || item.height) && (
                    <Typography level="body-sm">
                      {item.width || 'N/A'}mm × {item.height || 'N/A'}mm 
                    </Typography>
                  )}
                  <Typography level="body-sm">
                    Frame: {item.frameColor}, Glass: {item.glassType === 'clear' && item.clearGlassType 
                      ? `Clear (${item.clearGlassType.charAt(0).toUpperCase() + item.clearGlassType.slice(1)})`
                      : item.glassType === 'frosted' && item.obscuredGlassType
                        ? `Obscured (${item.obscuredGlassType})`
                        : item.glassType.charAt(0).toUpperCase() + item.glassType.slice(1)}
                  </Typography>
                  
                  {/* Display additional specifications */}
                  {item.profileType && (
                    <Typography level="body-sm">
                      Profile: {item.profileType}
                    </Typography>
                  )}
                  
                  {item.beadingType && (
                    <Typography level="body-sm">
                      Beading: {item.beadingType}
                    </Typography>
                  )}
                  
                  {item.cillIncluded && (
                    <Typography level="body-sm">
                      Cill included in height
                    </Typography>
                  )}
                  
                  {!item.cillIncluded && item.cillRequired && (
                    <Typography level="body-sm">
                      Cill required, not included in overall height
                      {item.cillType ? ` (${item.cillType}` : ''}
                      {item.cillHeight ? `, ${item.cillHeight}mm height)` : item.cillType ? ')' : ''}
                    </Typography>
                  )}
                  
                  {item.trickleVents && (
                    <Typography level="body-sm">
                      Trickle Vents: Yes
                    </Typography>
                  )}
                  
                  {item.notes && (
                    <Typography level="body-sm">
                      Notes: {item.notes}
                    </Typography>
                  )}
                  
                  <Typography level="body-sm" mt={1}>Qty: {item.quantity}</Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      )}
      
      {compact && (
        <Box sx={{ maxHeight: '150px', overflow: 'auto', mb: 2 }}>
          {items.map(item => (
            <Box 
              key={item.id} 
              sx={{ 
                mb: 1, 
                p: 1, 
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box 
                sx={{ 
                  width: 60,
                  height: 60,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mr: 1,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 'sm',
                }}
              >
                <Box 
                  component="img"
                  src={getWindowImagePath(item.windowType, item.windowStyle, item.imagePath)}
                  sx={{
                    maxWidth: '85%',
                    maxHeight: '85%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    // Try fallback image
                    const fallbackSrc = '/images/windows/Casements/Traditional/Traditional Style One.jpg';
                    if (fallbackSrc !== e.currentTarget.src) {
                      e.currentTarget.src = fallbackSrc;
                    } else {
                      // Replace with a placeholder if fallback fails
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        const placeholder = document.createElement('div');
                        placeholder.style.width = '100%';
                        placeholder.style.height = '100%';
                        placeholder.style.display = 'flex';
                        placeholder.style.alignItems = 'center';
                        placeholder.style.justifyContent = 'center';
                        placeholder.style.backgroundColor = '#f0f0f0';
                        placeholder.style.color = '#888';
                        placeholder.style.fontSize = '10px';
                        placeholder.textContent = 'Window';
                        parent.appendChild(placeholder);
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography level="body-sm" fontWeight="bold">
                  {item.windowType} - Style {item.windowStyle}
                </Typography>
                <Typography level="body-xs">Qty: {item.quantity}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WindowOrderSummary;